import React, { useState, useEffect, useRef } from 'react';
import { FaCloudUploadAlt, FaTrash, FaPlus, FaTimes, FaSave, FaSyncAlt } from 'react-icons/fa';
import { message, Slider, Switch } from 'antd';
import AdminService from '../services/adminService';

const UNIT_OPTIONS = [
  'None', 'pcs', 'Plate', 'Portion', 'Serving', 'Cup', 'Glass', 'Bowl', 'Slice',
  'g', 'kg', 'ml', 'l', 'pack', 'Packet', 'Bottle', 'Can', 'Box', 'mg', 'dozen', 'bag', 'set', 'Other'
];

const escapeRegex = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const cleanVariantBaseValue = (val, currentUnit) => {
  if (!val) return '';
  let str = String(val).trim();

  // 1. If currentUnit is provided and not 'none', strip it if at end
  if (currentUnit && currentUnit.trim() && currentUnit.toLowerCase() !== 'none') {
    const u = currentUnit.trim();
    const regex = new RegExp(`\\s+${escapeRegex(u)}$`, 'i');
    if (regex.test(str)) {
      const cand = str.replace(regex, '').trim();
      if (cand) str = cand;
    }
  }

  // 2. Check against UNIT_OPTIONS (skip single letters like 'l' unless length > 1, to avoid stripping 'L' for clothing)
  for (const unit of UNIT_OPTIONS) {
    if (unit && unit.toLowerCase() !== 'none' && unit.toLowerCase() !== 'other' && unit.length > 1) {
      const regex = new RegExp(`\\s+${escapeRegex(unit)}$`, 'i');
      if (regex.test(str)) {
        const cand = str.replace(regex, '').trim();
        if (cand) {
          str = cand;
          break;
        }
      }
    }
  }

  return str;
};

const formatVariantSizeLabel = (val, unit) => {
  const base = cleanVariantBaseValue(val, unit);
  const u = (unit || '').trim();
  if (base && u && u.toLowerCase() !== 'none') {
    return `${base} ${u}`;
  }
  return base || (u && u.toLowerCase() !== 'none' ? u : 'Default');
};

const EditProductModal = ({ onClose, onSave, businessId, itemId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    product_name: '',
    brand_name: '',
    category: '',
    category_id: '',
    sub_category: '',
    sub_category_id: '',
    description: '',
    is_customizable: false,
    is_featured: false,
    is_organic: false,
    pack_type: '',
    mfg_date: '',
    exp_date: '',
    age_range: [0, 100],
    age_category: '',
    material: '',
    fabric_type: '',
    dim_length: '',
    dim_breadth: '',
    dim_width: '',
    color: '',
    gender: '',
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [subImages, setSubImages] = useState([]);
  const [mediaImagesMap, setMediaImagesMap] = useState(null); // { image_key: url }
  const [subImageToDelete, setSubImageToDelete] = useState(null);
  const [variants, setVariants] = useState([]);
  const [taxSlabs, setTaxSlabs] = useState([]);
  const [apiCategories, setApiCategories] = useState([]);
  const [showImagesOptionsPage, setShowImagesOptionsPage] = useState(false);

  const fileInputRef = useRef(null);
  const subImageInputRef = useRef(null);
  const replaceSubImageInputRef = useRef(null);

  const [replaceTargetSubId, setReplaceTargetSubId] = useState(null);
  const [hoveredSubId, setHoveredSubId] = useState(null);

  // ── Fetch tax slabs & categories in parallel, then product detail ─────────
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [taxRes, catRes] = await Promise.all([
          AdminService.getTaxSlabs().catch(() => null),
          businessId ? AdminService.getBusinessCategories(businessId).catch(() => null) : Promise.resolve(null),
        ]);

        let slabs = [];
        if (taxRes?.success && taxRes?.data) slabs = taxRes.data;
        else if (Array.isArray(taxRes)) slabs = taxRes;
        setTaxSlabs(slabs);

        let cats = [];
        if (catRes?.success && catRes?.data) cats = catRes.data;
        else if (Array.isArray(catRes)) cats = catRes;
        setApiCategories(cats);

        // Now fetch product detail
        const res = await AdminService.getProductDetail(itemId);
        const p = res?.item || res?.data || res;
        // debug: show media shape in console for troubleshooting server keys
        try { console.debug('Product response payload:', res, 'product item:', p); } catch (_) { }

        setFormData({
          product_name: p.name || p.item_name || p.product_name || '',
          brand_name: p.brand_name || '',
          category: p.category || p.category_name || p.item_category || '',
          category_id: p.category_id ? String(p.category_id) : '',
          sub_category: p.sub_category_name || p.sub_category || '',
          sub_category_id: p.sub_category_id ? String(p.sub_category_id) : '',
          description: p.description || '',
          is_customizable: Boolean(p.is_customizable),
          is_featured: p.is_featured !== undefined ? Boolean(p.is_featured) : false,
          is_organic: Boolean(p.is_organic),
          pack_type: p.pack_type || '',
          mfg_date: p.mfg_date || '',
          exp_date: p.exp_date || '',
          age_range: (function () { try { return typeof p.age_range === 'string' ? JSON.parse(p.age_range) : (p.age_range || [0, 100]); } catch (e) { return [0, 100]; } })(),
          age_category: p.age_category || '',
          material: p.material || '',
          fabric_type: p.fabric_type || '',
          dim_length: p.dim_length || '',
          dim_breadth: p.dim_breadth || '',
          dim_width: p.dim_width || '',
          color: p.color || '',
          gender: p.gender || '',
        });

        // Existing main image and sub-images
        const media = p.media || {};
        const main = Array.isArray(media.images) && media.images.length > 0 ? media.images[0] : media.main_image || media.image || p.image || null;

        const subImagesData = res?.sub_images || p.sub_images || media.sub_images || {};
        let normalized = [];

        if (subImagesData && !Array.isArray(subImagesData) && typeof subImagesData === 'object') {
          normalized = Object.entries(subImagesData).map(([key, path]) => {
            let url = path;
            if (typeof path === 'string' && Array.isArray(media.images)) {
              const match = media.images.find((full) => typeof full === 'string' && (full.endsWith(path) || full.includes(path)));
              if (match) url = match;
            }
            return { key, url };
          });
          setMediaImagesMap(subImagesData);
        } else if (Array.isArray(media.images) && media.images.length > 1) {
          normalized = media.images.slice(1).map((url) => ({ key: null, url }));
        }

        setImagePreview(main || null);
        setSubImages(
          normalized.map((img) => ({ id: `${Date.now()}-${Math.random()}`, file: null, preview: img.url, key: img.key }))
        );

        // Map existing variants — include net_weight per variant
        if (Array.isArray(p.variants) && p.variants.length > 0) {
          setVariants(
            p.variants.map((v) => {
              const parsedUnit = v.unit || (typeof v.size_label === 'object' && v.size_label !== null ? v.size_label.unit : '') || v.net_weight_unit || '';

              const rawSizeLabel = typeof v.size_label === 'object' && v.size_label !== null
                ? (v.size_label.value !== undefined ? v.size_label.value : (v.size_label.label || v.size_label.size_label || ''))
                : (typeof v.size === 'object' && v.size !== null
                  ? (v.size.value !== undefined ? v.size.value : (v.size.label || v.size.size_label || ''))
                  : (v.value || v.size_label || v.size || v.variant_name || ''));

              const cleanBase = cleanVariantBaseValue(rawSizeLabel, parsedUnit);

              return {
                id: v.variant_id || Date.now() + Math.random(),
                variant_id: v.variant_id || null,
                size_label: cleanBase || rawSizeLabel,
                net_weight: String(v.net_weight ?? ''),
                unit: parsedUnit,
                pos_plu: v.pos_plu || v.plu_code || v.plu || '',
                stock_qty: String(v.stock_qty ?? v.stock_quantity ?? v.quantity ?? '0'),
                original_cost: String(v.original_cost ?? v.cost_price ?? ''),
                selling_price: String(v.selling_price ?? v.price ?? ''),
                gst: String(v.gst?.id || v.gst || '2'),
                color: v.color || '',
                gender: v.gender || '',
                is_active: v.is_active !== undefined ? Boolean(v.is_active) : (v.status !== undefined ? (v.status === '1' || v.status === 1 || v.status === 'active' || v.status === true) : true),
                is_visible: v.is_visible !== false,
                is_visible_counter: v.is_visible_counter !== false,
              };
            })
          );
        }
      } catch (err) {
        message.error('Failed to load product: ' + (err.message || 'Unknown error'));
      } finally {
        setLoading(false);
      }
    };

    if (itemId) init();
  }, [itemId, businessId]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'category') {
        next.sub_category = '';
        next.sub_category_id = '';
        const found = apiCategories.find((c) => c.category_name === value);
        next.category_id = found ? (found.category_id || found.id || '') : '';
      }
      if (name === 'sub_category') {
        const parentCat = apiCategories.find((c) => c.category_name === prev.category);
        const foundSub = parentCat?.sub_categories?.find((sc) => sc.category_name === value);
        next.sub_category_id = foundSub ? (foundSub.category_id || foundSub.id || '') : '';
      }
      return next;
    });
  };

  const handleImageClick = () => fileInputRef.current?.click();

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubImageClick = () => subImageInputRef.current?.click();

  const handleSubImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const newItems = files.map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      preview: URL.createObjectURL(file),
    }));
    setSubImages((prev) => [...prev, ...newItems]);
  };

  const handleReplaceSubImageChange = async (e) => {
    const file = e.target.files?.[0];
    const targetId = replaceTargetSubId;
    setReplaceTargetSubId(null);
    if (!file || !targetId) return;

    const img = subImages.find((i) => i.id === targetId);
    if (!img) return;

    // Determine server image key if available
    let imageKey = img.key || null;
    if (!imageKey && mediaImagesMap) {
      const found = Object.keys(mediaImagesMap).find((k) => {
        const v = mediaImagesMap[k];
        if (!v) return false;
        return v === img.preview || v === img.url || (typeof img.preview === 'string' && v.endsWith(img.preview)) || (typeof img.preview === 'string' && img.preview.endsWith(v));
      });
      if (found) imageKey = found;
    }

    const form = new FormData();
    if (imageKey) form.append('image_key', imageKey);
    form.append('sub_image', file);

    try {
      const res = await AdminService.updateProductSubImage(itemId, form);
      // Try to pick returned image URL from common response shapes
      const newUrl = res?.data?.image_url || res?.image_url || res?.new_image_url || res?.data?.url || res?.url || null;
      const updatedPreview = newUrl || URL.createObjectURL(file);

      setSubImages((prev) => prev.map((i) => (i.id === targetId ? { id: i.id, file: null, preview: updatedPreview, key: imageKey || i.key } : i)));
      message.success('Sub-image replaced');
      if (onSave) onSave();
    } catch (err) {
      message.error('Failed to replace sub-image: ' + (err.message || 'Unknown error'));
    } finally {
      // clear input value so same file can be selected again later
      if (replaceSubImageInputRef.current) replaceSubImageInputRef.current.value = '';
    }
  };

  const removeSubImage = (id) => {
    const img = subImages.find((item) => item.id === id);
    if (!img) return;
    // If the image has a File object it's a new (unsaved) upload — remove locally.
    if (img.file) {
      setSubImages((prev) => prev.filter((item) => item.id !== id));
    } else {
      // Existing image from server — ask for confirmation before deleting via API
      setSubImageToDelete(img);
    }
  };

  const handleConfirmDeleteSubImage = async () => {
    if (!subImageToDelete) return;
    const img = subImageToDelete;
    setSubImageToDelete(null);
    try {
      // call API to delete sub-image for this product — backend expects image_keys array
      let keys = [];
      if (img.key) keys = [img.key];
      else if (mediaImagesMap) {
        const found = Object.keys(mediaImagesMap).find((k) => {
          const v = mediaImagesMap[k];
          if (!v) return false;
          return v === img.preview || v === img.url || (typeof img.preview === 'string' && v.endsWith(img.preview)) || (typeof img.preview === 'string' && img.preview.endsWith(v));
        });
        if (found) keys = [found];
      }

      if (!keys.length) {
        message.error('Unable to delete image: missing server image key');
        return;
      }

      const payload = { image_keys: keys };
      console.debug('Deleting sub-image with payload:', payload, 'itemId:', itemId);
      const res = await AdminService.deleteProductSubImage(itemId, keys);
      if (res && (res.success || res.status === 'success' || (res.data && res.data.success !== false))) {
        message.success('Sub-image deleted');
        setSubImages((prev) => prev.filter((i) => i.id !== img.id));
        if (onSave) onSave();
      } else {
        message.error('Failed to delete image: ' + (res.message || 'Unknown error'));
      }
    } catch (err) {
      message.error('Error deleting image: ' + (err.message || 'Unknown error'));
    }
  };

  const addVariant = () =>
    setVariants((prev) => [
      ...prev,
      { id: Date.now(), variant_id: null, size_label: '', net_weight: '', unit: '', pos_plu: '', stock_qty: '0', original_cost: '', selling_price: '', gst: '2', color: '', gender: '', is_active: true, is_visible: true, is_visible_counter: true, _isNew: true },
    ]);

  const removeVariant = (id) => setVariants((prev) => prev.filter((v) => v.id !== id));

  const handleVariantChange = (id, field, value) =>
    setVariants((prev) =>
      prev.map((v) => {
        if (v.id !== id) return v;
        if (field === 'unit') {
          const cleanedSizeLabel = cleanVariantBaseValue(v.size_label, v.unit);
          return { ...v, unit: value, size_label: cleanedSizeLabel || v.size_label };
        }
        return { ...v, [field]: value };
      })
    );

  const [savingVariants, setSavingVariants] = useState(null);
  const [updatingVariant, setUpdatingVariant] = useState(null);
  const [deletingVariant, setDeletingVariant] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null); // local variant id pending confirmation

  // Save a brand-new variant (no variant_id yet)
  const handleSaveVariant = async (variantLocalId) => {
    const v = variants.find((x) => x.id === variantLocalId);
    if (!v) return;
    if (!v.size_label || !v.selling_price) {
      message.warning('Size Label and Selling Price are required');
      return;
    }

    if (Number(v.selling_price) <= 0) {
      message.warning('Selling Price must be greater than 0');
      return;
    }

    if (!v.original_cost || Number(v.original_cost) <= 0) {
      message.warning('Original Cost must be greater than 0');
      return;
    }

    if (v.stock_qty !== '' && Number(v.stock_qty) <= 0) {
      message.warning('Stock Quantity must be greater than 0');
      return;
    }
    setSavingVariants(variantLocalId);
    try {
      const unitVal = (v.unit || '').trim();
      const rawVal = cleanVariantBaseValue(v.value || v.size_label, unitVal);
      const combinedLabel = formatVariantSizeLabel(rawVal, unitVal);
      const sizeLabelObj = {
        unit: unitVal,
        value: rawVal || combinedLabel,
        size_label: combinedLabel,
      };

      const payload = new FormData();
      payload.append('size_label', JSON.stringify(sizeLabelObj));
      payload.append('size', JSON.stringify(sizeLabelObj));
      payload.append('value', rawVal || combinedLabel);
      payload.append('unit', unitVal);
      payload.append('net_weight', v.net_weight || '');
      payload.append('net_weight_unit', unitVal);
      payload.append('unit', unitVal);
      payload.append('pos_plu', v.pos_plu || '');
      payload.append('plu_code', v.pos_plu || '');
      payload.append('plu', v.pos_plu || '');
      payload.append('selling_price', v.selling_price);
      payload.append('original_cost', v.original_cost || '0');
      payload.append('gst', v.gst || '2');
      payload.append('stock_qty', v.stock_qty || '0');
      payload.append('is_visible', v.is_visible ? '1' : '0');
      payload.append('is_visible_counter', v.is_visible_counter ? '1' : '0');
      if (formData.category?.toLowerCase() === 'clothing') {
        payload.append('color', v.color || '');
        payload.append('gender', v.gender || '');
      }
      payload.append('is_active', v.is_active !== false ? 'true' : 'false');
      payload.append('status', v.is_active !== false ? '1' : '0');
      const res = await AdminService.addProductVariant(itemId, payload);
      if (res.success || res.status === 'success' || (res.data && res.data.success !== false)) {
        message.success('Variant added successfully');
        if (onSave) onSave();
        onClose();
      } else {
        message.error('Failed to add variant: ' + (res.message || 'Unknown error'));
      }
    } catch (err) {
      message.error('Error adding variant: ' + (err.message || 'Unknown error'));
    } finally {
      setSavingVariants(null);
    }
  };

  // Update an existing saved variant
  const handleUpdateVariant = async (variantLocalId) => {
    const v = variants.find((x) => x.id === variantLocalId);
    if (!v || !v.variant_id) return;
    if (!v.size_label || !v.selling_price) {
      message.warning('Size Label and Selling Price are required');
      return;
    }

    if (Number(v.selling_price) <= 0) {
      message.warning('Selling Price must be greater than 0');
      return;
    }

    if (!v.original_cost || Number(v.original_cost) <= 0) {
      message.warning('Original Cost must be greater than 0');
      return;
    }

    if (v.stock_qty !== '' && Number(v.stock_qty) <= 0) {
      message.warning('Stock Quantity must be greater than 0');
      return;
    }
    setUpdatingVariant(variantLocalId);
    try {
      const unitVal = (v.unit || '').trim();
      const rawVal = cleanVariantBaseValue(v.value || v.size_label, unitVal);
      const combinedLabel = formatVariantSizeLabel(rawVal, unitVal);
      const sizeLabelObj = {
        unit: unitVal,
        value: rawVal || combinedLabel,
        size_label: combinedLabel,
      };

      const payload = new FormData();
      payload.append('size_label', JSON.stringify(sizeLabelObj));
      payload.append('size', JSON.stringify(sizeLabelObj));
      payload.append('value', rawVal || combinedLabel);
      payload.append('unit', unitVal);
      payload.append('net_weight', v.net_weight || '');
      payload.append('net_weight_unit', unitVal);
      payload.append('unit', unitVal);
      payload.append('pos_plu', v.pos_plu || '');
      payload.append('plu_code', v.pos_plu || '');
      payload.append('plu', v.pos_plu || '');
      payload.append('selling_price', v.selling_price);
      payload.append('original_cost', v.original_cost || '0');
      payload.append('gst', v.gst || '2');
      payload.append('stock_qty', v.stock_qty || '0');
      payload.append('is_visible', v.is_visible ? '1' : '0');
      payload.append('is_visible_counter', v.is_visible_counter ? '1' : '0');
      if (formData.category?.toLowerCase() === 'clothing') {
        payload.append('color', v.color || '');
        payload.append('gender', v.gender || '');
      }
      payload.append('is_active', v.is_active !== false ? 'true' : 'false');
      payload.append('status', v.is_active !== false ? '1' : '0');
      const res = await AdminService.updateProductVariant(v.variant_id, payload);
      if (res.success || res.status === 'success' || (res.data && res.data.success !== false)) {
        message.success('Variant updated successfully');
        if (onSave) onSave();
        onClose();
      } else {
        message.error('Failed to update variant: ' + (res.message || 'Unknown error'));
      }
    } catch (err) {
      message.error('Error updating variant: ' + (err.message || 'Unknown error'));
    } finally {
      setUpdatingVariant(null);
    }
  };

  // Delete an existing saved variant
  const handleDeleteVariant = (variantLocalId) => {
    setDeleteConfirmId(variantLocalId); // open confirm modal
  };

  const handleConfirmDelete = async () => {
    const variantLocalId = deleteConfirmId;
    setDeleteConfirmId(null);
    const v = variants.find((x) => x.id === variantLocalId);
    if (!v || !v.variant_id) return;
    setDeletingVariant(variantLocalId);
    try {
      const res = await AdminService.deleteProductVariant(v.variant_id);
      if (res.success || res.status === 'success' || res.detail || (res.data && res.data.success !== false)) {
        message.success('Variant deleted successfully');
        if (onSave) onSave();
        onClose();
      } else {
        message.error('Failed to delete variant: ' + (res.message || 'Unknown error'));
      }
    } catch (err) {
      message.error('Error deleting variant: ' + (err.message || 'Unknown error'));
    } finally {
      setDeletingVariant(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.product_name) {
      message.warning('Product name is required');
      return;
    }

    if (variants.length > 0) {
      for (let i = 0; i < variants.length; i++) {
        const v = variants[i];
        if (v.selling_price !== '' && Number(v.selling_price) <= 0) {
          message.warning(`Variant ${i + 1}: Selling Price must be greater than 0`);
          return;
        }
        if (!v.original_cost || Number(v.original_cost) <= 0) {
          message.warning(`Variant ${i + 1}: Original Cost must be greater than 0`);
          return;
        }
        if (v.stock_qty !== '' && Number(v.stock_qty) <= 0) {
          message.warning(`Variant ${i + 1}: Stock Quantity must be greater than 0`);
          return;
        }
      }
    }

    if (!formData.category) {
      message.warning('Please select a Category');
      return;
    }

    const selectedCatObj = apiCategories.find(c => (c.category_name || '').trim().toLowerCase() === (formData.category || '').trim().toLowerCase());
    const subCategories = selectedCatObj?.sub_categories || [];
    if (subCategories.length > 0 && !formData.sub_category) {
      message.warning('Please select a Sub Category');
      return;
    }

    const selectedSubCatObj = subCategories.find(sc => (sc.category_name || '').trim().toLowerCase() === (formData.sub_category || '').trim().toLowerCase());
    const categoryId = selectedCatObj ? (selectedCatObj.category_id || selectedCatObj.id || '') : formData.category_id;
    const subCategoryId = selectedSubCatObj ? (selectedSubCatObj.category_id || selectedSubCatObj.id || '') : formData.sub_category_id;

    setSaving(true);

    // Build payload — use JSON so boolean/integer types are preserved correctly.
    // FormData coerces everything to strings which causes Django AutoField rejection on '0'.
    const body = {
      product_name: formData.product_name,
      brand_name: formData.brand_name,
      description: formData.description,
      is_customizable: formData.is_customizable,
      is_featured: formData.is_featured,
      is_organic: formData.is_organic,
      mfg_date: formData.mfg_date,
      exp_date: formData.exp_date,
    };

    if (formData.category?.toLowerCase() === 'clothing') {
      body.color = formData.color;
      body.gender = formData.gender;
      body.pack_type = formData.pack_type;
      body.age_range = JSON.stringify(formData.age_range);
      body.age_category = formData.age_category;
      body.material = formData.material;
      body.fabric_type = formData.fabric_type;
      body.dim_length = formData.dim_length;
      body.dim_breadth = formData.dim_breadth;
      body.dim_width = formData.dim_width;
    }

    body.item_category = formData.category;
    body.category_name = formData.category;
    if (categoryId) {
      body.category = parseInt(categoryId, 10);
      body.category_id = parseInt(categoryId, 10);
    }
    if (formData.sub_category) {
      body.sub_category = formData.sub_category;
    }
    if (subCategoryId) {
      body.sub_category_id = parseInt(subCategoryId, 10);
    }

    // If there's a file upload, use FormData so multipart data is preserved
    let payload;
    const needsFormData = imageFile || subImages.some((item) => item.file);
    if (needsFormData) {
      payload = new FormData();
      Object.entries(body).forEach(([k, v]) => payload.append(k, v));
      if (imageFile) payload.append('item_image', imageFile);
      subImages.filter((item) => item.file).forEach((item) => payload.append('sub_images', item.file));
    } else {
      payload = body;
    }

    try {
      const res = await AdminService.updateProduct(itemId, payload);
      if (res.success || res.status === 'success' || (res.data && res.data.success !== false)) {
        message.success('Product updated successfully');
        if (onSave) onSave();
        onClose();
      } else {
        message.error('Failed to update product: ' + (res.message || 'Unknown error'));
      }
    } catch (err) {
      message.error('Error updating product: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const overlayStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(0px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999, padding: '20px',
  };
  const modalStyle = {
    background: '#ffffff', color: '#111827', borderRadius: '8px',
    width: '100%', maxWidth: '900px', maxHeight: '90vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
    fontFamily: 'Inter, sans-serif',
  };
  const headerStyle = {
    padding: '20px', borderBottom: '1px solid #e5e7eb',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  };
  const bodyStyle = {
    padding: '20px', overflowY: 'auto', flex: 1,
    display: 'flex', flexDirection: 'column', gap: '20px',
  };
  const sectionStyle = {
    display: 'flex', flexDirection: 'column', gap: '20px',
  };
  const footerStyle = {
    padding: '16px 20px', borderTop: '1px solid #e5e7eb',
    display: 'flex', justifyContent: 'flex-end', gap: '10px',
  };
  const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' };
  const row3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' };
  const ig = { display: 'flex', flexDirection: 'column', gap: '5px' };
  const lbl = { fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left' };
  const inp = {
    background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px',
    padding: '9px 11px', color: '#111827', fontSize: '14px',
    width: '100%', boxSizing: 'border-box', outline: 'none',
  };
  const uploadBox = {
    border: '1px dashed #d1d5db', borderRadius: '6px', padding: '10px',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', cursor: 'pointer', background: '#f9fafb',
    color: '#3b82f6', gap: '6px', minHeight: '80px',
  };
  const vColStyle = { display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 };
  const vHdr = { fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' };

  const TaxOptions = () =>
    taxSlabs.length > 0
      ? taxSlabs.map((s) => <option key={s.id} value={s.id}>{s.tax_name}</option>)
      : (
        <>
          <option value="1">GST 0%</option>
          <option value="2">GST 5%</option>
          <option value="3">GST 12%</option>
          <option value="4">GST 18%</option>
          <option value="5">GST 28%</option>
        </>
      );

  // variant card: 4 fields per row
  const variantRow4 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' };
  const variantRow3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' };
  const variantRow2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' };
  const variantHdrStyle = { fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px', textAlign: 'left' };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={headerStyle}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>Edit Product</h2>
          <button
            style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '22px', cursor: 'pointer', padding: 0, lineHeight: 1 }}
            onClick={onClose} aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div style={bodyStyle}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0', color: '#6b7280' }}>
              Loading product data...
            </div>
          ) : (
            <>

              {showImagesOptionsPage ? (
                <div style={sectionStyle}>
                  <div style={ig}>
                    <div style={{ marginBottom: '12px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>Main Image</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                      <div style={{ ...uploadBox, minHeight: '160px', cursor: 'pointer' }} onClick={handleImageClick}>
                        {imagePreview ? (
                          <img src={imagePreview} alt="Main" style={{ maxHeight: '140px', borderRadius: '4px', objectFit: 'contain' }} />
                        ) : (
                          <>
                            <FaCloudUploadAlt size={22} />
                            <span style={{ fontSize: '12px', fontWeight: '500' }}>Main Image</span>
                          </>
                        )}
                      </div>
                      <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleImageChange} />
                    </div>
                  </div>

                  <div style={ig}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>Sub Images</span>
                      <button
                        type="button"
                        onClick={handleSubImageClick}
                        style={{
                          background: 'transparent', border: '1px solid #3b82f6', color: '#3b82f6',
                          padding: '5px 14px', borderRadius: '6px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: '500',
                        }}
                      >
                        <FaPlus size={10} /> Add Sub Images
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px' }}>
                      {subImages.length === 0 ? (
                        <div style={{ border: '1px dashed #d1d5db', borderRadius: '8px', minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', background: '#f9fafb' }}>
                          No sub images yet.
                        </div>
                      ) : subImages.map((img, idx) => (
                        <div key={img.id}
                          onMouseEnter={() => setHoveredSubId(img.id)}
                          onMouseLeave={() => setHoveredSubId(null)}
                          style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', background: '#f8fafc', minHeight: '120px' }}>
                          <img src={img.preview} alt={`Sub ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          {/* Hover overlay for Replace action */}
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: hoveredSubId === img.id ? 'auto' : 'none' }}>
                            <button
                              type="button"
                              onClick={() => {
                                setReplaceTargetSubId(img.id);
                                replaceSubImageInputRef.current?.click();
                              }}
                              style={{
                                padding: '6px 12px', borderRadius: '8px', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', cursor: 'pointer', opacity: hoveredSubId === img.id ? 1 : 0, transition: 'opacity 120ms ease-in-out'
                              }}
                            >
                              Replace
                            </button>
                          </div>
                          {img.key ? (
                            <div style={{ position: 'absolute', left: '8px', top: '8px', background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '2px 6px', borderRadius: '6px', fontSize: '11px' }}>
                              {img.key}
                            </div>
                          ) : (
                            <div style={{ position: 'absolute', left: '8px', top: '8px', background: 'rgba(255,69,58,0.95)', color: '#fff', padding: '2px 6px', borderRadius: '6px', fontSize: '11px' }}>
                              no-key
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => removeSubImage(img.id)}
                            style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%', width: '26px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                            aria-label="Remove image"
                          >
                            <FaTimes size={12} color="#ef4444" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <input type="file" ref={subImageInputRef} style={{ display: 'none' }} accept="image/*" multiple onChange={handleSubImagesChange} />
                    <input type="file" ref={replaceSubImageInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleReplaceSubImageChange} />
                  </div>

                  <div style={row2}>
                    <div style={ig}>
                      <label style={lbl}>Manufacturing Date</label>
                      <input type="date" style={inp} name="mfg_date" value={formData.mfg_date} onChange={handleChange} />
                    </div>
                    <div style={ig}>
                      <label style={lbl}>Expiry Date</label>
                      <input type="date" style={inp} name="exp_date" value={formData.exp_date} onChange={handleChange} />
                    </div>
                  </div>

                  <div style={ig}>
                    <span style={{ ...lbl, marginBottom: '8px' }}>Product Options</span>
                    <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '30px', justifyContent: 'flex-start', width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Switch checked={formData.is_featured} onChange={(checked) => setFormData(prev => ({ ...prev, is_featured: checked }))} size="small" />
                        <span style={{ fontSize: '13px', color: '#374151' }}>Featured</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Switch checked={formData.is_customizable} onChange={(checked) => setFormData(prev => ({ ...prev, is_customizable: checked }))} size="small" />
                        <span style={{ fontSize: '13px', color: '#374151' }}>Customizable</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Switch checked={formData.is_organic} onChange={(checked) => setFormData(prev => ({ ...prev, is_organic: checked }))} size="small" />
                        <span style={{ fontSize: '13px', color: '#374151' }}>Organic</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={sectionStyle}>
                  <div style={row3}>
                    <div style={ig}>
                      <label style={lbl}>Product Name *</label>
                      <input style={inp} name="product_name" value={formData.product_name} onChange={handleChange} placeholder="e.g., Basmati Rice" />
                    </div>
                    {(() => {
                      const selectedCatObj = apiCategories.find(
                        c => (c.category_name || '').trim().toLowerCase() === (formData.category || '').trim().toLowerCase()
                      );
                      const categoryValue = selectedCatObj ? selectedCatObj.category_name : formData.category;
                      const availableSubCategories = selectedCatObj?.sub_categories || [];
                      const selectedSubCatObj = availableSubCategories.find(
                        sc => (sc.category_name || '').trim().toLowerCase() === (formData.sub_category || '').trim().toLowerCase()
                      );
                      const subCategoryValue = selectedSubCatObj ? selectedSubCatObj.category_name : formData.sub_category;
                      const isSubCategoryRequired = availableSubCategories.length > 0;

                      return (
                        <>
                          <div style={ig}>
                            <label style={lbl}>Category *</label>
                            <select style={inp} name="category" value={categoryValue} onChange={handleChange}>
                              <option value="" disabled>Select Category</option>
                              {apiCategories.map((c, i) => (
                                <option key={i} value={c.category_name}>{c.category_name}</option>
                              ))}
                            </select>
                          </div>
                          <div style={ig}>
                            <label style={lbl}>
                              Sub Category {isSubCategoryRequired ? '*' : ''}
                            </label>
                            <select style={inp} name="sub_category" value={subCategoryValue} onChange={handleChange}>
                              <option value="">Select Sub Category</option>
                              {availableSubCategories.map((sc, i) => (
                                <option key={i} value={sc.category_name}>{sc.category_name}</option>
                              ))}
                            </select>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  <div style={row2}>
                    <div style={ig}>
                      <label style={lbl}>Brand Name</label>
                      <input style={inp} name="brand_name" value={formData.brand_name} onChange={handleChange} placeholder="e.g., Amul" />
                    </div>
                    <div style={ig}>
                      <label style={lbl}>Description</label>
                      <textarea
                        style={{ ...inp, minHeight: '70px', resize: 'none' }}
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Enter product description..."
                      />
                    </div>
                  </div>

                  {formData.category?.toLowerCase() === 'clothing' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '10px' }}>
                      <div style={row3}>
                        <div style={ig}>
                          <label style={lbl}>Color</label>
                          <select style={inp} name="color" value={formData.color} onChange={handleChange}>
                            <option value="">Select Color</option>
                            <option value="red">Red</option>
                            <option value="blue">Blue</option>
                            <option value="black">Black</option>
                            <option value="white">White</option>
                            <option value="green">Green</option>
                            <option value="yellow">Yellow</option>
                          </select>
                        </div>
                        <div style={ig}>
                          <label style={lbl}>Gender</label>
                          <select style={inp} name="gender" value={formData.gender} onChange={handleChange}>
                            <option value="">Select Gender</option>
                            <option value="men">Men</option>
                            <option value="women">Women</option>
                            <option value="unisex">Unisex</option>
                            <option value="kids">Kids</option>
                          </select>
                        </div>
                        <div style={ig}>
                          <label style={lbl}>Pack type</label>
                          <input style={inp} name="pack_type" value={formData.pack_type} onChange={handleChange} placeholder="e.g., Single, Pack of 3" />
                        </div>
                      </div>

                      <div style={ig}>
                        <label style={lbl}>Age Range (yrs)</label>
                        <div style={{ padding: '0 10px' }}>
                          <Slider
                            range
                            min={0}
                            max={100}
                            value={formData.age_range}
                            onChange={(val) => setFormData(prev => ({ ...prev, age_range: val }))}
                            trackStyle={[{ backgroundColor: '#3b82f6' }]}
                            handleStyle={[{ borderColor: '#3b82f6' }, { borderColor: '#3b82f6' }]}
                          />
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280', marginTop: '-10px' }}>
                            <span>0</span>
                            <span>18</span>
                            <span>45</span>
                            <span>100+</span>
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', alignItems: 'end' }}>
                        <div style={ig}>
                          <label style={lbl}>Age Category (Bucket)</label>
                          <input style={inp} name="age_category" value={formData.age_category} onChange={handleChange} />
                        </div>
                        <div style={ig}>
                          <label style={lbl}>Material (optional)</label>
                          <input style={inp} name="material" value={formData.material} onChange={handleChange} />
                        </div>
                        <div style={ig}>
                          <label style={lbl}>Fabric Type (optional)</label>
                          <input style={inp} name="fabric_type" value={formData.fabric_type} onChange={handleChange} />
                        </div>
                      </div>

                      <div style={{ position: 'relative', marginTop: '10px' }}>
                        <div style={{ borderTop: '1px solid #d1d5db', position: 'absolute', top: '50%', left: 0, right: 0, zIndex: 1 }}></div>
                        <div style={{ position: 'relative', zIndex: 2, background: '#f9fafb', display: 'inline-block', padding: '0 10px', left: '50%', transform: 'translateX(-50%)', color: '#374151', fontSize: '14px', fontWeight: '600' }}>Dimensions (cm)</div>
                      </div>

                      <div style={row3}>
                        <div style={ig}>
                          <label style={lbl}>Length</label>
                          <input style={inp} name="dim_length" value={formData.dim_length} onChange={handleChange} />
                        </div>
                        <div style={ig}>
                          <label style={lbl}>Breadth</label>
                          <input style={inp} name="dim_breadth" value={formData.dim_breadth} onChange={handleChange} />
                        </div>
                        <div style={ig}>
                          <label style={lbl}>Width</label>
                          <input style={inp} name="dim_width" value={formData.dim_width} onChange={handleChange} />
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ marginBottom: '18px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>Variant Details</span>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          type="button"
                          onClick={addVariant}
                          style={{
                            background: 'transparent', border: '1px solid #3b82f6', color: '#3b82f6',
                            padding: '5px 14px', borderRadius: '6px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: '500',
                          }}
                        >
                          <FaPlus size={10} /> Add Variant
                        </button>
                      </div>
                    </div>

                    {variants.length === 0 ? (
                      <div style={{
                        border: '1px dashed #d1d5db', borderRadius: '8px', padding: '24px',
                        textAlign: 'center', color: '#9ca3af', fontSize: '13px', background: '#f9fafb',
                      }}>
                        <p style={{ margin: 0 }}>No variants added yet.</p>
                        <p style={{ margin: '4px 0 0', fontSize: '12px' }}>Click "Add Variant" to add size or pack variations.</p>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {variants.map((v, idx) => (
                          <div key={v.id} style={{
                            background: '#f9fafb',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px', padding: '14px 14px 10px',
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Variant {idx + 1}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer', userSelect: 'none' }}>
                                  <Switch
                                    size="small"
                                    checked={v.is_active !== false}
                                    onChange={(checked) => handleVariantChange(v.id, 'is_active', checked)}
                                  />
                                  <span style={{ fontWeight: 500, fontSize: '12px', color: v.is_active !== false ? '#15803d' : '#6b7280' }}>
                                    {v.is_active !== false ? 'Active' : 'Inactive'}
                                  </span>
                                </label>
                                {v._isNew ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => handleSaveVariant(v.id)}
                                      disabled={savingVariants === v.id}
                                      style={{
                                        background: savingVariants === v.id ? '#9ca3af' : '#3b82f6',
                                        border: 'none', color: '#fff',
                                        padding: '4px 12px', borderRadius: '6px',
                                        cursor: savingVariants === v.id ? 'not-allowed' : 'pointer',
                                        fontSize: '12px', fontWeight: '600',
                                        display: 'flex', alignItems: 'center', gap: '5px',
                                      }}
                                    >
                                      <FaSave size={12} />
                                      {savingVariants === v.id ? 'Saving...' : 'Save Variant'}
                                    </button>
                                    <button type="button" onClick={() => removeVariant(v.id)}
                                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                                      <FaTrash size={11} /> Remove
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => handleUpdateVariant(v.id)}
                                      disabled={updatingVariant === v.id || deletingVariant === v.id}
                                      style={{
                                        background: updatingVariant === v.id ? '#9ca3af' : '#3b82f6',
                                        border: 'none', color: '#fff',
                                        padding: '4px 12px', borderRadius: '6px',
                                        cursor: updatingVariant === v.id || deletingVariant === v.id ? 'not-allowed' : 'pointer',
                                        fontSize: '12px', fontWeight: '600',
                                        display: 'flex', alignItems: 'center', gap: '5px',
                                      }}
                                    >
                                      <FaSyncAlt size={11} />
                                      {updatingVariant === v.id ? 'Updating...' : 'Update'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteVariant(v.id)}
                                      disabled={deletingVariant === v.id || updatingVariant === v.id}
                                      style={{
                                        background: 'none', border: '1px solid #ef4444', color: '#ef4444',
                                        padding: '4px 10px', borderRadius: '6px',
                                        cursor: deletingVariant === v.id || updatingVariant === v.id ? 'not-allowed' : 'pointer',
                                        fontSize: '12px',
                                        display: 'flex', alignItems: 'center', gap: '4px',
                                      }}
                                    >
                                      <FaTrash size={11} /> Delete
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                            {formData.category?.toLowerCase() === 'clothing' ? (
                              <>
                                <div style={variantRow3}>
                                  <div style={ig}>
                                    <label style={variantHdrStyle}>* SIZE</label>
                                    <input style={inp} value={v.size_label}
                                      onChange={(e) => handleVariantChange(v.id, 'size_label', e.target.value)}
                                      placeholder="e.g., XL" />
                                  </div>
                                  <div style={ig}>
                                    <label style={variantHdrStyle}>COLOR</label>
                                    <select style={inp} value={v.color || ''} onChange={(e) => handleVariantChange(v.id, 'color', e.target.value)}>
                                      <option value="">Select Color</option>
                                      <option value="red">Red</option>
                                      <option value="blue">Blue</option>
                                      <option value="black">Black</option>
                                      <option value="white">White</option>
                                      <option value="green">Green</option>
                                      <option value="yellow">Yellow</option>
                                    </select>
                                  </div>
                                  <div style={ig}>
                                    <label style={variantHdrStyle}>GENDER</label>
                                    <select style={inp} value={v.gender || ''} onChange={(e) => handleVariantChange(v.id, 'gender', e.target.value)}>
                                      <option value="">Select Gender</option>
                                      <option value="men">Men</option>
                                      <option value="women">Women</option>
                                      <option value="unisex">Unisex</option>
                                      <option value="kids">Kids</option>
                                    </select>
                                  </div>
                                </div>
                                <div style={{ ...variantRow2, marginTop: '10px' }}>
                                  <div style={ig}>
                                    <label style={variantHdrStyle}>* ORIGINAL COST (₹)</label>
                                    <div style={{ position: 'relative' }}>
                                      <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: '13px' }}>₹</span>
                                      <input style={{ ...inp, paddingLeft: '22px' }} type="number" value={v.original_cost} min="0.01" step="any" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                        onChange={(e) => handleVariantChange(v.id, 'original_cost', e.target.value)} placeholder="0.00" />
                                    </div>
                                  </div>
                                  <div style={ig}>
                                    <label style={variantHdrStyle}>* SELLING PRICE (₹)</label>
                                    <div style={{ position: 'relative' }}>
                                      <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: '13px' }}>₹</span>
                                      <input style={{ ...inp, paddingLeft: '22px' }} type="number" value={v.selling_price} min="0.01" step="any" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                        onChange={(e) => handleVariantChange(v.id, 'selling_price', e.target.value)} placeholder="0.00" />
                                    </div>
                                  </div>
                                </div>
                                <div style={{ ...variantRow2, marginTop: '10px' }}>
                                  <div style={ig}>
                                    <label style={variantHdrStyle}>* GST (%)</label>
                                    <select style={inp} value={v.gst} onChange={(e) => handleVariantChange(v.id, 'gst', e.target.value)}>
                                      <TaxOptions />
                                    </select>
                                  </div>
                                  <div style={ig}>
                                    <label style={variantHdrStyle}>STOCK QTY</label>
                                    <input style={inp} type="number" value={v.stock_qty} min="1" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                      onChange={(e) => handleVariantChange(v.id, 'stock_qty', e.target.value)}
                                      placeholder="0" />
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>
                                <div style={variantRow4}>
                                  <div style={ig}>
                                    <label style={variantHdrStyle}>* Size Label</label>
                                    <input style={inp} value={v.size_label}
                                      onChange={(e) => handleVariantChange(v.id, 'size_label', e.target.value)}
                                      placeholder="e.g., Regular" />
                                  </div>
                                  <div style={ig}>
                                    <label style={variantHdrStyle}>Unit</label>
                                    <select style={inp} value={v.unit || ''} onChange={(e) => handleVariantChange(v.id, 'unit', e.target.value)}>
                                      {UNIT_OPTIONS.map((u) => <option key={u} value={u === 'None' ? '' : u}>{u}</option>)}
                                    </select>
                                  </div>
                                  <div style={ig}>
                                    <label style={variantHdrStyle}>Net Weight</label>
                                    <input style={inp} type="number" value={v.net_weight} min="0"
                                      onChange={(e) => handleVariantChange(v.id, 'net_weight', e.target.value)}
                                      placeholder="e.g., 750" />
                                  </div>
                                  <div style={ig}>
                                    <label style={variantHdrStyle}>Stock Qty</label>
                                    <input style={inp} type="number" value={v.stock_qty} min="1" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                      onChange={(e) => handleVariantChange(v.id, 'stock_qty', e.target.value)}
                                      placeholder="0" />
                                  </div>
                                </div>
                                <div style={{ ...variantRow4, marginTop: '10px' }}>
                                  <div style={ig}>
                                    <label style={variantHdrStyle}>* Original Cost (₹)</label>
                                    <div style={{ position: 'relative' }}>
                                      <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: '13px' }}>₹</span>
                                      <input style={{ ...inp, paddingLeft: '22px' }} type="number" value={v.original_cost} min="0.01" step="any" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                        onChange={(e) => handleVariantChange(v.id, 'original_cost', e.target.value)} placeholder="0.00" />
                                    </div>
                                  </div>
                                  <div style={ig}>
                                    <label style={variantHdrStyle}>* Selling Price (₹)</label>
                                    <div style={{ position: 'relative' }}>
                                      <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: '13px' }}>₹</span>
                                      <input style={{ ...inp, paddingLeft: '22px' }} type="number" value={v.selling_price} min="0.01" step="any" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                        onChange={(e) => handleVariantChange(v.id, 'selling_price', e.target.value)} placeholder="0.00" />
                                    </div>
                                  </div>
                                  <div style={ig}>
                                    <label style={variantHdrStyle}>* GST (%)</label>
                                    <select style={inp} value={v.gst} onChange={(e) => handleVariantChange(v.id, 'gst', e.target.value)}>
                                      <TaxOptions />
                                    </select>
                                  </div>
                                  <div style={ig}>
                                    <label style={variantHdrStyle}>POS PLU</label>
                                    <input style={inp} value={v.pos_plu || ''}
                                      onChange={(e) => handleVariantChange(v.id, 'pos_plu', e.target.value)}
                                      placeholder="Optional PLU code" />
                                  </div>
                                </div>
                              </>
                            )}
                            <div style={{ display: 'flex', flexDirection: 'row', gap: '16px', marginTop: '10px' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
                                <Switch size="small" checked={v.is_visible !== false} onChange={(checked) => handleVariantChange(v.id, 'is_visible', checked)} />
                                Visible
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
                                <Switch size="small" checked={v.is_visible_counter !== false} onChange={(checked) => handleVariantChange(v.id, 'is_visible_counter', checked)} />
                                Visible Counter
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button type="button" onClick={onClose}
            style={{ background: '#f9fafb', border: '1px solid #d1d5db', color: '#374151', padding: '8px 20px', borderRadius: '6px', fontWeight: '500', cursor: 'pointer', fontSize: '14px' }}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => setShowImagesOptionsPage((prev) => !prev)}
            style={{
              background: showImagesOptionsPage ? '#eff6ff' : '#2563eb',
              color: showImagesOptionsPage ? '#1d4ed8' : '#ffffff',
              border: '1px solid #93c5fd',
              borderRadius: '8px',
              padding: '8px 16px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
            }}
          >
            {showImagesOptionsPage ? 'Previous' : 'Next'}
          </button>
          <button type="button" onClick={handleSubmit} disabled={saving || loading}
            style={{ background: saving || loading ? '#9ca3af' : '#3b82f6', border: 'none', color: '#fff', padding: '8px 20px', borderRadius: '6px', fontWeight: '500', cursor: saving || loading ? 'not-allowed' : 'pointer', fontSize: '14px' }}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* ── Variant Delete Confirmation Modal ── */}
      {deleteConfirmId && (
        <div
          onClick={() => setDeleteConfirmId(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', display: 'flex',
            justifyContent: 'center', alignItems: 'center', zIndex: 10000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: '16px', padding: '32px',
              maxWidth: '420px', width: '90%',
              boxShadow: '0 20px 50px rgba(0,0,0,0.25)', textAlign: 'center',
            }}
          >
            {/* Icon */}
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%',
              background: '#fee2e2', display: 'flex', alignItems: 'center',
              justifyContent: 'center', margin: '0 auto 16px',
            }}>
              <FaTrash size={22} color="#ef4444" />
            </div>

            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '700', color: '#1f2937' }}>
              Delete Variant?
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#6b7280', lineHeight: '1.5' }}>
              Are you sure you want to delete this variant?
            </p>

            {/* Warning box */}
            <div style={{
              background: '#fefce8', border: '1px solid #fde047',
              borderRadius: '10px', padding: '14px 16px',
              marginBottom: '24px', textAlign: 'left',
            }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#854d0e', lineHeight: '1.6' }}>
                ⚠️ <strong>Warning:</strong> This variant may be linked to existing orders, cart items, or other records.
                Deleting it could cause errors or data inconsistencies in those areas.
                Proceed only if you are sure this variant is no longer in use.{' '}
                <strong>May lead to app crash.</strong>
              </p>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setDeleteConfirmId(null)}
                style={{
                  padding: '10px 24px', border: '1px solid #d1d5db',
                  borderRadius: '8px', background: '#fff', color: '#374151',
                  cursor: 'pointer', fontWeight: '500', fontSize: '14px',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                style={{
                  padding: '10px 24px', border: 'none',
                  borderRadius: '8px', background: '#ef4444', color: '#fff',
                  cursor: 'pointer', fontWeight: '600', fontSize: '14px',
                }}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Sub-image Delete Confirmation Modal ── */}
      {subImageToDelete && (
        <div
          onClick={() => setSubImageToDelete(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', display: 'flex',
            justifyContent: 'center', alignItems: 'center', zIndex: 10000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: '16px', padding: '32px',
              maxWidth: '420px', width: '90%',
              boxShadow: '0 20px 50px rgba(0,0,0,0.25)', textAlign: 'center',
            }}
          >
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%',
              background: '#fee2e2', display: 'flex', alignItems: 'center',
              justifyContent: 'center', margin: '0 auto 16px',
            }}>
              <FaTrash size={22} color="#ef4444" />
            </div>

            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '700', color: '#1f2937' }}>
              Delete Image?
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#6b7280', lineHeight: '1.5' }}>
              Are you sure you want to delete this sub-image?
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setSubImageToDelete(null)}
                style={{
                  padding: '10px 24px', border: '1px solid #d1d5db',
                  borderRadius: '8px', background: '#fff', color: '#374151',
                  cursor: 'pointer', fontWeight: '500', fontSize: '14px',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeleteSubImage}
                style={{
                  padding: '10px 24px', border: 'none',
                  borderRadius: '8px', background: '#ef4444', color: '#fff',
                  cursor: 'pointer', fontWeight: '600', fontSize: '14px',
                }}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProductModal;
